import type { LatLngTuple } from "@fortex/api";
import { distanceMeters } from "../geo/geodesy";

export type SplitResult = {
  kept: LatLngTuple[];
  remainder: LatLngTuple[];
};

function samePoint(a: LatLngTuple, b: LatLngTuple): boolean {
  return Math.abs(a[0] - b[0]) < 1e-9 && Math.abs(a[1] - b[1]) < 1e-9;
}

export function reversePoints(points: LatLngTuple[]): LatLngTuple[] {
  return [...points].reverse();
}

export function movePoints(points: LatLngTuple[], deltaLat: number, deltaLng: number): LatLngTuple[] {
  return points.map(a => [a[0] + deltaLat, a[1] + deltaLng]);
}

export function insertPoint(points: LatLngTuple[], index: number, point: LatLngTuple): LatLngTuple[] {
  const next = [...points];

  next.splice(Math.max(0, Math.min(points.length, index)), 0, point);

  return next;
}

export function replacePoint(points: LatLngTuple[], index: number, point: LatLngTuple): LatLngTuple[] {
  return points.map((a, current) => (current === index ? point : a));
}

export function removePoint(points: LatLngTuple[], index: number): LatLngTuple[] {
  return points.filter((_, current) => current !== index);
}

export function splitLine(points: LatLngTuple[], segmentIndex: number, point: LatLngTuple, keep: "start" | "end" = "start"): SplitResult {
  if (points.length < 2 || segmentIndex < 0 || segmentIndex >= points.length - 1) return { kept: [...points], remainder: [] };

  const head = points.slice(0, segmentIndex + 1);
  const tail = points.slice(segmentIndex + 1);
  const start = samePoint(head[head.length - 1], point) ? head : [...head, point];
  const end = samePoint(tail[0], point) ? tail : [point, ...tail];

  return keep === "start" ? { kept: start, remainder: end } : { kept: end, remainder: start };
}

export function mergeLines(base: LatLngTuple[], other: LatLngTuple[]): LatLngTuple[] {
  if (base.length === 0) return [...other];
  if (other.length === 0) return [...base];

  const baseStart = base[0];
  const baseEnd = base[base.length - 1];
  const otherStart = other[0];
  const otherEnd = other[other.length - 1];
  const options: { distance: number; build: () => LatLngTuple[] }[] = [
    { distance: distanceMeters(baseEnd, otherStart), build: () => [...base, ...other] },
    { distance: distanceMeters(baseEnd, otherEnd), build: () => [...base, ...reversePoints(other)] },
    { distance: distanceMeters(baseStart, otherEnd), build: () => [...other, ...base] },
    { distance: distanceMeters(baseStart, otherStart), build: () => [...reversePoints(other), ...base] }
  ];

  options.sort((a, b) => a.distance - b.distance);

  const merged = options[0].build();
  const result: LatLngTuple[] = [];

  for (const point of merged) {
    if (result.length === 0 || !samePoint(result[result.length - 1], point)) result.push(point);
  }

  return result;
}
